import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import api from "@/lib/api";
import { hydrateSession, isHydratedUser, fetchCurrentUser } from "@/contexts/authHydration";
import { getEntitlements, entitlementsReady } from "@/lib/entitlements";
import { authDebug, summarizeMeUser } from "@/lib/authDebug";
import { shouldBumpSessionKey } from "@/lib/sessionIdentity";

const AuthContext = createContext(null);

export const TOKEN_KEY = "smartqr_token";

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Remount key for dashboard trees — bumps on account switch / logout / login. */
  const [sessionKey, setSessionKey] = useState(0);
  /** Bumped on every auth transition so in-flight /auth/me responses cannot overwrite newer state. */
  const sessionEpochRef = useRef(0);
  const prevUserIdRef = useRef(null);

  const bumpSessionIfIdentityChanged = useCallback((nextUser) => {
    const nextId = nextUser?.id || null;
    const prevId = prevUserIdRef.current;
    if (shouldBumpSessionKey(prevId, nextId)) {
      prevUserIdRef.current = nextId;
      setSessionKey((k) => k + 1);
      authDebug("sessionKey bumped — identity change", { from: prevId, to: nextId });
    }
  }, []);

  const applyUserIfCurrent = useCallback((epoch, me) => {
    if (epoch !== sessionEpochRef.current) {
      authDebug("session epoch rejected", { epoch, current: sessionEpochRef.current, me: summarizeMeUser(me) });
      return false;
    }
    setUser(me);
    bumpSessionIfIdentityChanged(me);
    authDebug("session epoch accepted", { epoch, me: summarizeMeUser(me) });
    return true;
  }, [bumpSessionIfIdentityChanged]);

  const markReady = useCallback((epoch, reason) => {
    if (epoch !== sessionEpochRef.current) return;
    setLoading(false);
    authDebug("auth state marked ready", { epoch, reason });
  }, []);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      sessionEpochRef.current += 1;
      setUser(null);
      bumpSessionIfIdentityChanged(null);
      setLoading(false);
      authDebug("no stored token — auth ready (logged out)");
      return null;
    }
    const epoch = ++sessionEpochRef.current;
    setLoading(true);
    authDebug("token restore hydration started", { epoch });
    try {
      const me = await hydrateSession(api, { clearToken });
      if (!applyUserIfCurrent(epoch, me)) return null;
      return me;
    } catch {
      if (epoch === sessionEpochRef.current) {
        clearToken();
        setUser(null);
        bumpSessionIfIdentityChanged(null);
        authDebug("token restore failed — session cleared", { epoch });
      }
      return null;
    } finally {
      markReady(epoch, "loadUser");
    }
  }, [applyUserIfCurrent, bumpSessionIfIdentityChanged, markReady]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Cross-tab: when another tab logs in/out, converge on the shared token.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== TOKEN_KEY) return;
      authDebug("storage token change from other tab", {
        hasToken: Boolean(event.newValue),
      });
      if (!event.newValue) {
        sessionEpochRef.current += 1;
        setUser(null);
        bumpSessionIfIdentityChanged(null);
        setLoading(false);
        return;
      }
      loadUser();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [loadUser, bumpSessionIfIdentityChanged]);

  // Soft entitlement refresh when returning to the tab (admin plan switches, etc.).
  useEffect(() => {
    let timer;
    const softRefresh = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const epochAtStart = sessionEpochRef.current;
        try {
          authDebug("soft entitlement refresh");
          const me = await fetchCurrentUser(api);
          if (epochAtStart !== sessionEpochRef.current) {
            authDebug("soft refresh discarded — epoch moved");
            return;
          }
          setUser(me);
          // Same user: do not bump sessionKey (preserve page state); entitlements update in place.
          authDebug("soft refresh applied", summarizeMeUser(me));
        } catch {
          authDebug("soft refresh failed (ignored)");
        }
      }, 250);
    };
    document.addEventListener("visibilitychange", softRefresh);
    window.addEventListener("focus", softRefresh);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", softRefresh);
      window.removeEventListener("focus", softRefresh);
    };
  }, []);

  const login = async (email, password) => {
    const epoch = ++sessionEpochRef.current;
    setLoading(true);
    setUser(null);
    bumpSessionIfIdentityChanged(null);
    authDebug("login started", { epoch, email });
    try {
      const res = await api.post("/auth/login", { email, password });
      const token = res.data?.token;
      if (!token) {
        throw new Error("Login did not return a token");
      }
      localStorage.setItem(TOKEN_KEY, token);
      authDebug("token stored", { epoch });
      const me = await hydrateSession(api, { clearToken });
      if (!applyUserIfCurrent(epoch, me)) {
        return { token, user: me };
      }
      return { token, user: me };
    } catch (err) {
      if (epoch === sessionEpochRef.current) {
        clearToken();
        setUser(null);
        bumpSessionIfIdentityChanged(null);
        authDebug("login failed — session cleared", { epoch });
      }
      throw err;
    } finally {
      markReady(epoch, "login");
    }
  };

  const signup = async (email, password, turnstileToken = "", companyWebsite = "") => {
    const epoch = ++sessionEpochRef.current;
    setLoading(true);
    setUser(null);
    bumpSessionIfIdentityChanged(null);
    authDebug("signup started", { epoch, email });
    try {
      const res = await api.post("/auth/signup", {
        email,
        password,
        turnstile_token: turnstileToken,
        company_website: companyWebsite,
      });
      const token = res.data?.token;
      if (!token) {
        throw new Error("Signup did not return a token");
      }
      localStorage.setItem(TOKEN_KEY, token);
      authDebug("token stored", { epoch });
      const me = await hydrateSession(api, { clearToken });
      if (!applyUserIfCurrent(epoch, me)) {
        return { token, user: me, message: res.data?.message };
      }
      return { token, user: me, message: res.data?.message };
    } catch (err) {
      if (epoch === sessionEpochRef.current) {
        clearToken();
        setUser(null);
        bumpSessionIfIdentityChanged(null);
        authDebug("signup failed — session cleared", { epoch });
      }
      throw err;
    } finally {
      markReady(epoch, "signup");
    }
  };

  const acceptSession = async (token, _nextUser) => {
    const epoch = ++sessionEpochRef.current;
    setLoading(true);
    setUser(null);
    bumpSessionIfIdentityChanged(null);
    authDebug("acceptSession started", { epoch });
    try {
      if (!token) {
        throw new Error("Missing session token");
      }
      localStorage.setItem(TOKEN_KEY, token);
      authDebug("token stored", { epoch });
      const me = await hydrateSession(api, { clearToken });
      if (!applyUserIfCurrent(epoch, me)) return me;
      return me;
    } catch (err) {
      if (epoch === sessionEpochRef.current) {
        clearToken();
        setUser(null);
        bumpSessionIfIdentityChanged(null);
        authDebug("acceptSession failed — session cleared", { epoch });
      }
      throw err;
    } finally {
      markReady(epoch, "acceptSession");
    }
  };

  const logout = () => {
    sessionEpochRef.current += 1;
    clearToken();
    setUser(null);
    bumpSessionIfIdentityChanged(null);
    setLoading(false);
    authDebug("logout — auth ready (logged out)", { epoch: sessionEpochRef.current });
  };

  const refreshUser = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      sessionEpochRef.current += 1;
      setUser(null);
      bumpSessionIfIdentityChanged(null);
      return null;
    }
    const epoch = ++sessionEpochRef.current;
    authDebug("refreshUser started", { epoch });
    try {
      const me = await hydrateSession(api, { clearToken });
      if (!applyUserIfCurrent(epoch, me)) return me;
      authDebug("auth state marked ready", { epoch, reason: "refreshUser" });
      return me;
    } catch {
      if (epoch === sessionEpochRef.current) {
        clearToken();
        setUser(null);
        bumpSessionIfIdentityChanged(null);
      }
      return null;
    }
  };

  const entitlements = getEntitlements(user);
  const ready = entitlementsReady(user);

  const value = useMemo(
    () => ({
      user,
      loading,
      authLoading: loading || (!!user && !ready),
      entitlements,
      entitlementsReady: ready,
      isHydrated: isHydratedUser(user),
      sessionKey,
      login,
      signup,
      acceptSession,
      logout,
      setUser,
      refreshUser,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, entitlements, ready, sessionKey],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
